import React, { useEffect, useRef, useState } from 'react';
import {
  Keyboard,
  KeyboardType,
  NativeSyntheticEvent,
  StyleProp,
  StyleSheet,
  TextInput,
  TextInputKeyPressEventData,
  View,
  ViewStyle,
} from 'react-native';
import Icon from 'shared/components/vxl/icon/Icon/Icon';
import NumberPad from 'shared/components/vxl/NumberPad/NumberPad';
import { DELETE } from 'shared/components/vxl/NumberPad/NumberPadButton';
import BodyXSText from 'shared/components/vxl/typography/primary/body/BodyXSText';
import theme from 'shared/styles/theme';

import CharacterInputField from './CharacterInputField';
import { Variants } from './FixedCharacterInput.constants';
import { characterArray } from './helpers';

const BACKSPACE = 'Backspace';
const TAB = 'Tab';

export interface FixedCharacterInputProps {
  inputBoxLength?: number;
  autoFocusOnLoad?: boolean;
  keyboardAppearance?: 'default' | 'light' | 'dark';
  keyboardType?: KeyboardType;
  clearInputs?: boolean;
  code?: string;
  onCodeChanged?: (code: string) => void;
  onCodeFilled?: (code: string) => void;
  style?: StyleProp<ViewStyle>;
  variant: Variants;
  sendOtp?: (otp: string) => void;
  sendDate?: (date: string) => void;
  hasError?: boolean;
  errorMessage?: string;
  /* If we want to use NumberPad VXL component */
  isCustomNumPad?: boolean;
  numPadSubmitButtonLabel?: string;
  numPadSubmitButtonLoading?: boolean;
  showNumPadSubmitButton?: boolean;
  shouldResetInput?: boolean;
  onResetComplete?: () => void;
}

const FixedCharacterInput = ({
  inputBoxLength = 6,
  autoFocusOnLoad = true,
  keyboardAppearance = 'default',
  keyboardType = 'number-pad',
  clearInputs = false,
  code = '',
  onCodeChanged,
  onCodeFilled,
  style,
  sendOtp,
  variant,
  sendDate,
  errorMessage,
  isCustomNumPad = false,
  numPadSubmitButtonLabel = '',
  numPadSubmitButtonLoading = false,
  showNumPadSubmitButton = true,
  shouldResetInput = false,
  onResetComplete,
}: FixedCharacterInputProps) => {
  const boxCount = variant === Variants.Date ? 4 : inputBoxLength;
  const [digits, setDigits] = useState(characterArray(code));
  const [selectedIndex, setSelectedIndex] = useState(0);
  const fields = useRef<Array<TextInput>>([]);

  useEffect(() => {
    setDigits(characterArray(code));
  }, [code]);

  useEffect(() => {
    if (shouldResetInput) {
      setDigits(Array(boxCount).fill(''));
      setSelectedIndex(0);
      focusField(0);
      onResetComplete?.();
    }
  }, [shouldResetInput, boxCount, onResetComplete]);

  useEffect(() => {
    if (boxCount === digits.length) {
      notifyCodeChanged(digits.join(''));
      blurAllFields();
    }
    bringUpKeyboardIfNeeded();
    const keyboardDidHideListener = Keyboard.addListener(
      'keyboardDidHide',
      blurAllFields,
    );
    return () => keyboardDidHideListener.remove();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boxCount]);

  const bringUpKeyboardIfNeeded = () => {
    if (autoFocusOnLoad) {
      focusField(Math.max(digits.length - 1, 0));
    }
  };

  const notifyCodeChanged = (code: string) => {
    onCodeChanged?.(code);
    sendOtp?.(code);
    sendDate?.(code);
  };

  const handleChangeText = (index: number, text: string) => {
    const newDigits = [...digits];
    const oldTextLength = newDigits[index] ? newDigits[index].length : 0;
    const newTextLength = text.length;
    if (newTextLength - oldTextLength === boxCount) {
      newDigits.splice(
        index,
        boxCount,
        ...text.split('').slice(oldTextLength, newTextLength),
      );
      setDigits(newDigits);
      notifyCodeChanged(newDigits.join(''));
    } else {
      if (text.length === 0) {
        newDigits[index] = '';
      } else {
        text.split('').forEach((value, idx) => {
          if (index < boxCount) {
            newDigits[index + idx] = value;
          }
        });
      }
      setDigits(newDigits);
      notifyCodeChanged(newDigits.join(''));
    }

    const result = newDigits.join('');

    if (result.length >= boxCount) {
      onCodeFilled?.(result);
      focusField(boxCount - 1);
      blurAllFields();
    } else {
      if (text.length > 0 && index < boxCount - 1) {
        focusField(index + 1);
      }
    }
  };

  const handleKeyPressTextInput = (index: number, key: string) => {
    if (key === 'Backspace' && !digits[index] && index > 0) {
      handleChangeText(index - 1, '');
      focusField(index - 1);
    } else if (key === 'Backspace' && digits[index]?.length > 0) {
      handleChangeText(index, '');
    }
  };

  const focusField = (index: number) => {
    if (index < fields.current.length) {
      fields.current[index]?.focus();
      setSelectedIndex(index);
    }
  };

  const blurAllFields = () => {
    fields.current.forEach((field) => field?.blur?.());
    setSelectedIndex(-1);
  };

  useEffect(() => {
    focusField(0);
  }, []);

  const handleNumpadPress = (value: string) => {
    const newOtp = [...digits];
    if (selectedIndex === 0 && value === 'DELETE') {
      return;
    }
    if (
      selectedIndex === 0 &&
      value.length === boxCount &&
      newOtp[1] === undefined
    ) {
      setDigits([...value]);
    } else if (value === DELETE) {
      handleOnKeyPress(undefined, value);
    } else if (selectedIndex < boxCount) {
      newOtp[selectedIndex] = value;
      setDigits(newOtp);
      if (selectedIndex < boxCount - 1) {
        setSelectedIndex(selectedIndex + 1);
      }
      focusField(selectedIndex + 1);
    }
    onCodeChanged?.(newOtp.join(''));
  };

  const handleOnKeyPress = (
    e: NativeSyntheticEvent<TextInputKeyPressEventData> | undefined,
    customNumPadKey: string | undefined,
  ) => {
    if (e?.nativeEvent?.key === BACKSPACE || customNumPadKey === DELETE) {
      const newDigits = [...digits];
      if (newDigits[selectedIndex]) {
        newDigits[selectedIndex] = '';
        setSelectedIndex(selectedIndex);
        focusField(selectedIndex);
      } else {
        newDigits[selectedIndex - 1] = '';
        setSelectedIndex(selectedIndex - 1);
        focusField(selectedIndex - 1);
      }
      setDigits(newDigits);
    } else if (e?.nativeEvent.key === TAB) {
      e.preventDefault();
      if (selectedIndex < boxCount && selectedIndex !== boxCount - 1) {
        focusField(selectedIndex + 1);
      } else if (selectedIndex === boxCount - 1) {
        focusField(0);
      }
    }
  };

  const errorStyle =
    variant === Variants.Otp ? styles.errorWrapper : styles.dateError;
  const container = { ...styles.container, ...(style as ViewStyle) };
  const withNumpadStyle = isCustomNumPad ? styles.errorContainer : { flex: 1 };

  return (
    <View style={container}>
      <View style={withNumpadStyle}>
        <View style={styles.inputWrapper}>
          {Array.from({ length: boxCount }, (_, index) => (
            <CharacterInputField
              index={index}
              clearInputs={clearInputs}
              digits={digits}
              fields={fields}
              focusField={focusField}
              handleChangeText={handleChangeText}
              handleKeyPressTextInput={handleKeyPressTextInput}
              keyboardAppearance={keyboardAppearance}
              keyboardType={keyboardType}
              isSelected={selectedIndex === index}
              variant={variant}
              errorMessage={errorMessage}
              key={'character-input-field-' + index}
              isCustomNumPad={isCustomNumPad}
            />
          ))}
        </View>
        {errorMessage ? (
          <View style={errorStyle}>
            <View style={styles.icon}>
              <Icon
                iconKey="STATUS_ERROR"
                iconColor={theme.color.status.negative}
                iconSize={theme.spacing.m}
              />
            </View>
            <BodyXSText style={styles.errorText} numberOfLines={0}>
              {errorMessage}
            </BodyXSText>
          </View>
        ) : null}
      </View>
      {isCustomNumPad ? (
        <View>
          <NumberPad
            submitDisabled={digits?.join('')?.length !== boxCount}
            submitButtonLabel={numPadSubmitButtonLabel}
            handleButtonPress={handleNumpadPress}
            handleSubmit={() => {
              sendOtp?.(digits.join(''));
              sendDate?.(digits.join(''));
            }}
            disabled={false}
            submitButtonLoading={numPadSubmitButtonLoading}
            showSubmitButton={showNumPadSubmitButton}
            showsDoubleZero={false}
          />
        </View>
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flexDirection: 'column',
    flex: 1,
  },
  dateError: {
    alignSelf: 'center',
    color: theme.color.status.negative,
    flexDirection: 'row',
    justifyContent: 'flex-start',
    marginTop: theme.spacing.s,
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
  },
  errorText: {
    color: theme.color.status.negative,
    flexShrink: 1,
  },
  errorWrapper: {
    alignContent: 'center',
    alignItems: 'baseline',
    color: theme.color.status.negative,
    flexDirection: 'row',
    marginTop: theme.spacing.s,
  },
  icon: {
    alignSelf: 'center',
    paddingRight: theme.spacing.xs,
  },
  inputWrapper: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: theme.spacing.s,
    justifyContent: 'center',
  },
});

export default FixedCharacterInput;
